import type { Me, ProjectRole } from "../api/types";
import { currentRoleLabel } from "../crew/crewLabels";

/**
 * Capability codes (Settings → Permissions, contract 0.10.36): what a role may do outside a workflow move. The server
 * decides every one through `PermissionAccess.Can`; the SPA asks `session.can(projectKey, code)` only to decide whether a
 * control is shown. Never test a role name for these.
 */
export const CAPABILITIES = {
  itemCreate: "item.create",
  itemEdit: "item.edit",
  itemDelete: "item.delete",
  itemMove: "item.move",
  itemComment: "item.comment",
  itemInternalNote: "item.internal_note",
  itemCommentDelete: "item.comment_delete",
  itemLink: "item.link",
  itemAttach: "item.attach",
  itemLogTime: "item.log_time",
  crDesignEdit: "cr.design_edit",
  serviceDeskReply: "sd.reply",
  cmView: "cm.view",
  cmFieldsEdit: "cm.fields_edit",
  cmMonthClose: "cm.month_close",
  cmMonthReopen: "cm.month_reopen",
  sprintManage: "sprint.manage",
  /** Batch 16 (GRF-25): put items into an open sprint, move them between sprints or take them out (sprint.manage includes it). */
  sprintAssign: "sprint.assign",
  boardCreate: "board.create",
  boardMode: "board.mode",
  /** Revision 89 (ruling 2026-10-03): Emergency Approve on a deployment, every internal project role by default. */
  deploymentEmergencyApprove: "deployment.emergency_approve",
  /** Revision 107 amendment: create Issue and Action items without item.create (the ATC group by default). */
  itemCreateRaid: "item.create_raid",
  /** Revision 107 amendment (Risk since Revision 110): update (edit, comment, note) Issue, Action and Risk items without item.edit (the ATC group by default). */
  itemEditRaid: "item.edit_raid",
  /** Batch 16 (ruling 2026-10-09): export the Items list to Excel; not a write, every role by default. */
  itemExport: "item.export",
  /** Batch 19 F3: see a project's events and their lists (read-only); every internal project role by default, never a partner. */
  eventView: "event.view",
  /** Batch 19 F3: create, edit, close, reopen and delete events and manage their lists (Chief Pilot, Capt. and QA by default). */
  eventManage: "event.manage",
  /** Batch 19 F1 (ruling R8): give external testers access; a row locked to the Chief Pilot and the Capt. */
  eventExternalManage: "event.external.manage",
  // Batch 20 B20-1 (sidebar by role): one read-only code per project sidebar entry (Settings → Permissions, group "Sidebar menu").
  // The server never checks them; they decide only whether the entry is shown (`screens.ts` navGate).
  menuChangeManagement: "menu.change_management",
  menuIssuesActions: "menu.issues_actions",
  menuBacklog: "menu.backlog",
  menuBoard: "menu.board",
  menuReports: "menu.reports",
  /** ATC (Admin) and ATC (Infra) are locked off (L21). */
  menuServiceDesk: "menu.service_desk",
  /** ANDed with event.view on the Events entry. */
  menuEvents: "menu.events",
  menuDeployments: "menu.deployments",
  menuDeploymentBacklog: "menu.deployment_backlog",
  /** The RAID placeholder: no default holder. */
  menuRaid: "menu.raid",
  // Batch 26 (weekly project status, rulings of 20 Oct 2026): the project's Capt.s and Chief Pilot only (and a System Admin);
  // every row is locked to its roles (L22–L25).
  /** The Project status sidebar entry (L22). */
  menuProjectStatus: "menu.project_status",
  /** See the weekly status page, its frozen weeks and its deck (L22). */
  statusView: "status.view",
  /** Write the week's report, mark it reported or "no change", attach the deck (L23). */
  statusEdit: "status.edit",
  /** Every project's status reports and the missing list: the Chief Pilot (L24). */
  statusPortfolio: "status.portfolio",
  /** Freeze a week (freeze_week): the Chief Pilot (L24). */
  statusFreeze: "status.freeze",
  /** Re-freeze a frozen week: a System Admin only, in the browser (L25). */
  statusRefreeze: "status.refreeze",
  /** Batch 26 R2 (ruling of 22 Oct 2026): comment on the week's status page — the Capt., the Chief Pilot and ATC (PMO) (L26). */
  statusComment: "status.comment",
  // Batch 26 R2 (rulings of 22 Oct 2026). A named status contributor holds status.view, status.rows and menu.project_status on their
  // project through the contributor list (the server's StatusContributorGrant), never through a matrix cell.
  /** Add, edit and remove the report's rows, and mark Issues and Actions for it (L28). */
  statusRows: "status.rows",
  /** Name and remove the project's status contributors (L29). */
  statusContributors: "status.contributors",
  /** Batch 26 R2 (PMO review P1, P3): decide the weekly RAG and take the meeting copy — the Chief Pilot only (L30). */
  statusDecide: "status.decide",
  // Wiki W1 (WIKI-SPEC-20261025a §6.1): internal staff only; every Partner cell is locked off (L-WIKI-INTERNAL), the FO never publishes
  // (L-WIKI-AI), restricting and managing are the Chief Pilot's, the Capt.'s and ATC (Admin)'s (L-WIKI-LEADS).
  wikiView: "wiki.view",
  wikiEdit: "wiki.edit",
  wikiPublish: "wiki.publish",
  wikiComment: "wiki.comment",
  wikiRestrict: "wiki.restrict",
  wikiManage: "wiki.manage",
  wikiExport: "wiki.export",
  /** The project Wiki sidebar entry (ANDed with wiki.view). */
  menuWiki: "menu.wiki",
  /** The Company wiki sidebar entry. */
  menuCompanyWiki: "menu.company_wiki",
  // Presentations (PRESENT-SPEC-20261028 §8.3): project members only; every Partner cell is locked off, never DevOps.
  presentView: "present.view",
  presentUpload: "present.upload",
  presentControl: "present.control",
  /** The project Presentations sidebar entry (ANDed with present.view). */
  menuPresentations: "menu.presentations",
} as const;

export type Capability = (typeof CAPABILITIES)[keyof typeof CAPABILITIES];

const CAPABILITY_CODES: ReadonlySet<string> = new Set(Object.values(CAPABILITIES));

export function isCapability(code: string): boolean {
  return CAPABILITY_CODES.has(code);
}

/**
 * A sidebar code (batch 20 B20-1, server `PermissionCatalog.IsMenuCode`). Ruling of 17 Oct 2026 (batch 21 C2): the
 * SystemAdmin bypass never covers one, so a SystemAdmin's sidebar comes from their project roles only.
 */
export function isMenuCode(code: string): boolean {
  return code.startsWith("menu.");
}

/**
 * R13-D01: what each capability lets a role do, the server's `PermissionCatalog.ActionPhrases` word for word (pinned by
 * `permissions.test.ts`). The matrix can give any code to any role, so screen copy about a missing capability names the
 * action and where it is set — never the default role list, which is untrue as soon as a SystemAdmin changes the matrix.
 */
export const CAPABILITY_ACTIONS: Readonly<Record<Capability, string>> = {
  "item.create": "create items",
  "item.edit": "edit this item",
  "item.delete": "delete items",
  "item.move": "move items to another project",
  "item.comment": "write public comments",
  "item.internal_note": "add internal notes",
  "item.comment_delete": "delete comments",
  "item.link": "add or remove links",
  "item.attach": "upload attachments or change their category",
  "item.log_time": "log time",
  "cr.design_edit": "edit the CR design fields",
  "sd.reply": "reply to the requester",
  "cm.view": "open the CM report",
  "cm.fields_edit": "edit the CM report fields",
  "cm.month_close": "close a CM report month",
  "cm.month_reopen": "reopen a CM report month",
  "sprint.manage": "manage sprints or rank the backlog",
  "sprint.assign": "put items into a sprint",
  "board.create": "create boards",
  "board.mode": "change the board mode",
  "deployment.emergency_approve": "emergency-approve a deployment",
  "item.create_raid": "create Issue and Action items",
  "item.edit_raid": "update Issue, Action and Risk items",
  "item.export": "export items to Excel",
  "event.view": "see events",
  "event.manage": "manage events and their lists",
  "event.external.manage": "give external testers access to events",
  "menu.change_management": "see Change management in the menu",
  "menu.issues_actions": "see Issues and actions in the menu",
  "menu.backlog": "see Backlog in the menu",
  "menu.board": "see Board in the menu",
  "menu.reports": "see Reports in the menu",
  "menu.service_desk": "see Service desk in the menu",
  "menu.events": "see Events in the menu",
  "menu.deployments": "see Deployments in the menu",
  "menu.deployment_backlog": "see Deployment backlog in the menu",
  "menu.raid": "see RAID in the menu",
  "menu.project_status": "see Project status in the menu",
  "status.view": "see the weekly project status",
  "status.edit": "edit the weekly project status",
  "status.portfolio": "see the status reports of every project",
  "status.freeze": "freeze a status week",
  "status.refreeze": "re-freeze a status week",
  "status.comment": "comment on the weekly project status",
  "status.rows": "change the rows of the weekly project status",
  "status.contributors": "name the status contributors",
  "status.decide": "decide the weekly RAG and take the meeting copy",
  "wiki.view": "read the project wiki",
  "wiki.edit": "write wiki pages",
  "wiki.publish": "publish wiki pages",
  "wiki.comment": "comment on wiki pages",
  "wiki.restrict": "restrict wiki pages",
  "wiki.manage": "manage the project wiki",
  "wiki.export": "export wiki pages",
  "menu.wiki": "see Wiki in the menu",
  "menu.company_wiki": "see Company wiki in the menu",
  "present.view": "see presentations",
  "present.upload": "upload or change presentations",
  "present.control": "present",
  "menu.presentations": "see Presentations in the menu",
};

/** The second sentence of every matrix refusal (server `PermissionAccess.WhereToChange`). */
export const WHERE_TO_CHANGE = "A System Admin sets this in Settings, Permissions.";

/** Batch 19 ruling R7 (server `PermissionWords.AskCaptain`): whom to ask, ending the event capabilities' matrix refusal. */
export const ASK_CAPTAIN = "Ask your project's Capt. if you need this.";

/** The matrix capabilities whose refusal ends with {@link ASK_CAPTAIN} (server `PermissionAccess.AskCaptainCodes`). */
const ASK_CAPTAIN_CODES: ReadonlySet<Capability> = new Set<Capability>(["event.view", "event.manage"]);

/** O-R15-1: the second sentence when the caller's every cell is locked off by different rules (server `PermissionAccess.FixedRule`). */
export const FIXED_RULE = "This is a fixed rule and cannot be changed in Settings, Permissions.";

/** Capabilities that are not writes (server `CapabilityRule.IsWrite` false): a Viewer's cell is not locked for them. */
const READS: ReadonlySet<Capability> = new Set<Capability>([
  "cm.view",
  "item.export",
  "event.view",
  "menu.change_management",
  "menu.issues_actions",
  "menu.backlog",
  "menu.board",
  "menu.reports",
  "menu.service_desk",
  "menu.events",
  "menu.deployments",
  "menu.deployment_backlog",
  "menu.raid",
  "menu.project_status",
  "status.view",
  "status.portfolio",
  "wiki.view",
  "wiki.export",
  "menu.wiki",
  "menu.company_wiki",
  "present.view",
  "menu.presentations",
]);

/** Codes a partner never holds (server `CapabilityRule.PartnerLocked`). */
const PARTNER_LOCKED: ReadonlySet<Capability> = new Set<Capability>([
  "item.edit",
  "item.delete",
  "item.move",
  "item.internal_note",
  "item.comment_delete",
  "cr.design_edit",
  "sd.reply",
  "cm.view",
  "cm.fields_edit",
  "cm.month_close",
  "cm.month_reopen",
  "sprint.manage",
  "sprint.assign",
  "board.create",
  "board.mode",
  "deployment.emergency_approve",
  "item.create_raid",
  "item.edit_raid",
  "event.view",
  "event.manage",
  "event.external.manage",
  "menu.deployment_backlog",
  "menu.raid",
  "menu.project_status",
  "status.view",
  "status.edit",
  "status.portfolio",
  "status.freeze",
  "status.refreeze",
  "status.comment",
  "status.rows",
  "status.contributors",
  "status.decide",
  "wiki.view",
  "wiki.edit",
  "wiki.publish",
  "wiki.comment",
  "wiki.restrict",
  "wiki.manage",
  "wiki.export",
  "menu.wiki",
  "menu.company_wiki",
  "present.view",
  "present.upload",
  "present.control",
  "menu.presentations",
]);

/** Wiki W1 (D1): the wiki rows, whose Partner cell carries the wiki's own lock (server `PermissionCatalog.WikiCodes`). */
const WIKI_CODES: ReadonlySet<Capability> = new Set<Capability>([
  "wiki.view",
  "wiki.edit",
  "wiki.publish",
  "wiki.comment",
  "wiki.restrict",
  "wiki.manage",
  "wiki.export",
  "menu.wiki",
  "menu.company_wiki",
]);

/** Codes the DevOps column never holds, lock L2 (server `CapabilityRule.DevOpsLocked`). */
const DEVOPS_LOCKED: ReadonlySet<Capability> = new Set<Capability>([
  "item.create",
  "item.delete",
  "item.move",
  "deployment.emergency_approve",
  "sprint.assign",
  "event.view",
  "event.manage",
  "event.external.manage",
  "menu.project_status",
  "status.view",
  "status.edit",
  "status.portfolio",
  "status.freeze",
  "status.refreeze",
  "status.comment",
  "status.rows",
  "status.contributors",
  "status.decide",
  "wiki.publish",
  "wiki.restrict",
  "wiki.manage",
  "present.view",
  "present.upload",
  "present.control",
  "menu.presentations",
]);

/** Rows locked to a set of roles (server `CapabilityRule.OnlyRoles`); every other column is locked off. */
const ONLY_ROLES: Readonly<Partial<Record<Capability, readonly string[]>>> = {
  "cm.month_reopen": ["ProjectDirector"],
  "sprint.manage": ["ProjectDirector", "ProjectManager"],
  "event.external.manage": ["ProjectDirector", "ProjectManager"],
  // Batch 26 (rulings of 20 Oct 2026): the weekly status. status.refreeze is the System Admin's alone (no column holds it).
  // Batch 26 R2 (ruling of 22 Oct 2026): ATC (PMO) reads every status page and comments, never edits it.
  "menu.project_status": ["ProjectDirector", "ProjectManager", "ATC"],
  "status.view": ["ProjectDirector", "ProjectManager", "ATC"],
  "status.edit": ["ProjectDirector", "ProjectManager"],
  "status.portfolio": ["ProjectDirector", "ATC"],
  "status.freeze": ["ProjectDirector"],
  "status.refreeze": [],
  "status.comment": ["ProjectDirector", "ProjectManager", "ATC"],
  "status.rows": ["ProjectDirector", "ProjectManager"],
  "status.contributors": ["ProjectDirector", "ProjectManager"],
  "status.decide": ["ProjectDirector"],
  // Wiki W1 (§6.1): the Chief Pilot, the Capt. and ATC (Admin).
  "wiki.restrict": ["ProjectDirector", "ProjectManager", "ProjectAdmin"],
  "wiki.manage": ["ProjectDirector", "ProjectManager", "ProjectAdmin"],
};

/** The lock of each row locked to a set of roles (server `PermissionLocks.RowLock`). */
const ROW_LOCKS: Readonly<Partial<Record<Capability, "L8" | "L16" | "L20" | "L22" | "L23" | "L24" | "L25" | "L26" | "L27" | "L28" | "L29" | "L30" | "L-WIKI-LEADS">>> = {
  "cm.month_reopen": "L8",
  "sprint.manage": "L16",
  "event.external.manage": "L20",
  "menu.project_status": "L22",
  "status.view": "L22",
  "status.edit": "L23",
  "status.portfolio": "L27",
  "status.freeze": "L24",
  "status.refreeze": "L25",
  "status.comment": "L26",
  "status.rows": "L28",
  "status.contributors": "L29",
  "status.decide": "L30",
  "wiki.restrict": "L-WIKI-LEADS",
  "wiki.manage": "L-WIKI-LEADS",
};

/** Batch 20: single cells locked off for some roles (server `CapabilityRule.LockedOffRoles`): the Service desk entry, L21; wiki W1:
 * the FO's wiki.publish cell, L-WIKI-AI. */
const LOCKED_OFF_CELLS: Readonly<Partial<Record<Capability, { roles: readonly string[]; lock: "L21" | "L-WIKI-AI" }>>> = {
  "menu.service_desk": { roles: ["ProjectAdmin", "InfraManager"], lock: "L21" },
  "wiki.publish": { roles: ["FirstOfficer"], lock: "L-WIKI-AI" },
};

/** The reasons of the cell locks a refusal can name (server `PermissionLocks`, word for word; pinned by `permissions.test.ts`). */
export const CELL_LOCK_REASONS: Readonly<Record<"L2" | "L5" | "L6" | "L21" | "L-WIKI-AI" | "L-WIKI-INTERNAL", string>> = {
  L2: "The DevOps grant gives deployment and DevOps-queue powers only; it is not a project team role.",
  L5: "Partners never hold this (partner isolation, ADR-14; a partner reply would stop the response clock).",
  L6: "A Viewer is read-only (R10-D03). Give the person the Member role instead.",
  L21:
    "ATC (Admin) and ATC (Infra) do not see the Service desk entry (batch 20 sidebar ruling); a person who also holds another role that has it still sees it.",
  "L-WIKI-AI": "An AI co-pilot never publishes. Only a person can publish a wiki page. An AI assistant writes drafts; a person reviews and publishes them.",
  "L-WIKI-INTERNAL": "The wiki is for internal staff only.",
};

/** The lock of a locked-OFF cell, or null when a System administrator may tick it (server `PermissionLocks.IsLockedOff` + `CellLock`). */
function lockedOffCell(code: Capability, role: string): keyof typeof CELL_LOCK_REASONS | NonNullable<(typeof ROW_LOCKS)[Capability]> | null {
  const only = ONLY_ROLES[code];
  if (only) return only.includes(role) ? null : (ROW_LOCKS[code] ?? "L16");
  const cell = LOCKED_OFF_CELLS[code];
  if (cell?.roles.includes(role)) return cell.lock;
  if (role === "Partner" && WIKI_CODES.has(code)) return "L-WIKI-INTERNAL";
  if (role === "Viewer" && !READS.has(code)) return "L6";
  if (role === "Partner" && PARTNER_LOCKED.has(code)) return "L5";
  if (role === "DevOps" && DEVOPS_LOCKED.has(code)) return "L2";
  return null;
}

/**
 * The words for a capability the caller lacks, as the server's 403 says them (`PermissionAccess.RefusalFor`): a Viewer and a
 * write, or a partner and a partner-locked code, keep their own fixed words; a row locked to a set of roles keeps its words;
 * and (O-R15-1) when EVERY role the caller holds here sits on a locked-off cell — a DevOps grant alone and `item.create` (L2) —
 * no System administrator can set it, so the refusal gives the lock's reason instead of pointing at Settings. `roles` are the
 * caller's role codes on the project, a DevOps grant included as "DevOps" (`rolesWithGrantIn`); without them the matrix decides.
 */
export function capabilityRefusal(code: Capability, who: { viewer?: boolean; partner?: boolean; atc?: boolean; roles?: readonly string[] } = {}): string {
  const action = CAPABILITY_ACTIONS[code];
  if (who.partner && WIKI_CODES.has(code)) return CELL_LOCK_REASONS["L-WIKI-INTERNAL"];
  if (who.partner && PARTNER_LOCKED.has(code)) return `Partners cannot ${action}.`;
  // Batch 19 ruling R7 (D-B19-08): the event capabilities name whom to ask on the read-only path too (server `PermissionAccess.Refuse`).
  if (who.viewer && !READS.has(code)) return ASK_CAPTAIN_CODES.has(code) ? `${readOnlyRefusal(action, who)} ${ASK_CAPTAIN}` : readOnlyRefusal(action, who);
  if (code === "cm.month_reopen") return CM_MONTH_REOPEN_REFUSAL;
  if (code === "sprint.manage") return SPRINT_MANAGE_REFUSAL;
  if (code === "event.external.manage") return EVENT_EXTERNAL_MANAGE_REFUSAL;
  const status = STATUS_REFUSALS[code] ?? WIKI_REFUSALS[code];
  if (status) return status;
  const roles = (who.roles ?? []).filter((role) => role !== "SystemAdmin");
  const locks = roles.map((role) => lockedOffCell(code, role));
  if (roles.length > 0 && locks.every((lock) => lock !== null)) {
    const distinct = [...new Set(locks)];
    const only = distinct.length === 1 ? distinct[0] : null;
    const reason = only && only in CELL_LOCK_REASONS ? CELL_LOCK_REASONS[only as keyof typeof CELL_LOCK_REASONS] : FIXED_RULE;
    return `Your role on this project does not allow you to ${action}. ${reason}`;
  }
  return `Your role on this project does not allow you to ${action}. ${ASK_CAPTAIN_CODES.has(code) ? ASK_CAPTAIN : WHERE_TO_CHANGE}`;
}

/** The short reason a read-only caller is read-only, for "(…)" in a sentence: the Viewer's crew label, "ATC", or "no role here". */
export function readOnlyWhy(roles: readonly string[], atc: boolean): string {
  if (roles.includes("Viewer")) return currentRoleLabel("Viewer");
  if (atc) return currentRoleLabel("ATC");
  return roles.length === 0 ? "no role on this project" : currentRoleLabel("Viewer");
}

/**
 * Why a read-only caller is read-only, as the server's `PermissionAccess.ReadOnlyDetail` words it (B14 OBS-1: an ATC was told
 * "(Viewer)"): a Viewer "(Passenger · Viewer)" from the crew label table; the ATC grant with no role here "(ATC (PMO))"; no role at
 * all says so. `roles` undefined means unknown, and then a Viewer is assumed (the only project role that is read-only).
 */
export function readOnlyRefusal(action: string, who: { atc?: boolean; roles?: readonly string[] } = {}): string {
  const roles = who.roles?.filter((role) => role !== "SystemAdmin");
  if (roles?.includes("Viewer") || (!who.atc && roles === undefined)) return `You have read-only access to this project (${currentRoleLabel("Viewer")}), so you cannot ${action}.`;
  if (who.atc) return `You have read-only access to this project (${currentRoleLabel("ATC")}), so you cannot ${action}.`;
  return roles && roles.length === 0 ? `You hold no role on this project, so you can read it but cannot ${action}.` : `You have read-only access to this project, so you cannot ${action}.`;
}

/** True when the refusal names a rule no Settings change lifts (it does not send the reader to Settings, Permissions). */
export function isFixedRefusal(refusal: string): boolean {
  return !refusal.endsWith(WHERE_TO_CHANGE) && !refusal.endsWith(ASK_CAPTAIN);
}

/** The row locked to the Project director (ruling 2026-10-02): not the matrix's, so its words name the role. */
export const CM_MONTH_REOPEN_REFUSAL = "Reopening a CM report month needs the Chief Pilot role on this project, or System Admin.";

/** The row locked to the Project director and Project manager (ruling 2026-10-03), the server's words. */
export const SPRINT_MANAGE_REFUSAL =
  "Managing sprints or ranking the backlog needs the Chief Pilot or Capt. role on this project, or System Admin.";

/** Batch 19 ruling R8: the row locked to the Project director and Project manager, the server's words. */
export const EVENT_EXTERNAL_MANAGE_REFUSAL =
  "Giving external testers access needs the Chief Pilot or Capt. role on this project, or System Admin.";

/** Batch 26 (rulings of 20 Oct 2026): the weekly status rows, the server's words (`PermissionAccess.LockedRowRefusals`). */
export const STATUS_REFUSALS: Readonly<Partial<Record<Capability, string>>> = {
  "status.view": "The weekly project status is for the Chief Pilot, the Capt. and the status contributors of this project, ATC (PMO), or System Admin.",
  "menu.project_status": "The weekly project status is for the Chief Pilot, the Capt. and the status contributors of this project, ATC (PMO), or System Admin.",
  "status.edit": "Writing the weekly project status needs the Chief Pilot or Capt. role on this project, or System Admin.",
  "status.portfolio": "The status reports of every project are for the Chief Pilot, ATC (PMO), or System Admin.",
  "status.freeze": "Freezing a status week needs the Chief Pilot role, or System Admin.",
  "status.refreeze": "Re-freezing a status week is for a System Admin only.",
  "status.comment": "Commenting on the weekly project status needs the Chief Pilot or Capt. role on this project, ATC (PMO), or System Admin.",
  "status.rows":
    "Changing the rows of the weekly project status needs the Chief Pilot or Capt. role on this project, a place as its status contributor, or System Admin.",
  "status.contributors": "Naming status contributors needs the Chief Pilot or Capt. role on this project, or System Admin.",
  "status.decide": "Deciding the weekly RAG or taking the meeting copy needs the Chief Pilot role on this project, or System Admin.",
};

/** Wiki W1 (§6.1): the rows locked to the Chief Pilot, the Capt. and ATC (Admin), the server's words (`PermissionAccess.LockedRowRefusals`). */
export const WIKI_REFUSALS: Readonly<Partial<Record<Capability, string>>> = {
  "wiki.restrict": "Restricting wiki pages needs the Chief Pilot, Capt. or ATC (Admin) role on this project, or System Admin.",
  "wiki.manage": "Managing the project wiki needs the Chief Pilot, Capt. or ATC (Admin) role on this project, or System Admin.",
};

// Revision 107: ProjectAdmin ("ATC (Admin)") holds nothing by default (read-only, like Viewer).
const WRITERS: readonly ProjectRole[] = ["ProjectDirector", "ProjectManager", "Analyst", "Member", "QA", "ServiceDeskAgent", "Partner"];
const INTERNAL: readonly ProjectRole[] = ["ProjectDirector", "ProjectManager", "Analyst", "Member", "QA", "ServiceDeskAgent"];
const EDITORS: readonly ProjectRole[] = ["ProjectDirector", "ProjectManager", "Analyst", "Member", "QA", "ServiceDeskAgent"];
const SERVICE_DESK: readonly ProjectRole[] = ["ServiceDeskAgent", "Analyst", "ProjectManager", "ProjectDirector"];

/**
 * The default matrix by project role (db/seed/reference.sql), used ONLY when the server sent no `projectPermissions`
 * (an older server, or a test fixture that builds `Me` by hand). DevOps is an organisation grant `projectRoles` never lists,
 * so it is absent here as it was from the role checks this replaces. `permissions.test.ts` pins it to the seed.
 */
/** Revision 104: the First Officer (AI co-pilot) — "reply + create + update"; Revision 106 adds its pictures (item.attach). */
const FIRST_OFFICER: readonly ProjectRole[] = ["FirstOfficer"];

/** Revision 107: InfraManager ("ATC (Infra)") holds what the DevOps column holds (comments, notes, links, attachments, time). */
const INFRA: readonly ProjectRole[] = ["InfraManager"];

/** Batch 20 B20-1: the project roles that saw every ungated sidebar entry before batch 20 (all but ATC (Admin) and ATC (Infra)). */
const MENU_CREW: readonly ProjectRole[] = [...EDITORS, "FirstOfficer", "Viewer", "Partner"];

export const DEFAULT_CAPABILITY_HOLDERS: Readonly<Record<Capability, readonly ProjectRole[]>> = {
  "item.create": [...WRITERS, ...FIRST_OFFICER],
  "item.edit": [...EDITORS, ...FIRST_OFFICER],
  // Revision 87: the Project Director holds every code the Project Manager holds.
  "item.delete": ["ProjectManager", "ProjectDirector"],
  "item.move": ["ProjectManager", "ProjectDirector"],
  "item.comment": [...WRITERS, ...FIRST_OFFICER, ...INFRA],
  "item.internal_note": [...INTERNAL, ...INFRA],
  "item.comment_delete": ["ProjectManager", "ProjectDirector"],
  "item.link": [...WRITERS, ...INFRA],
  // Revision 106 (B14 OBS-3): the First Officer attaches the pictures in what it writes.
  "item.attach": [...WRITERS, ...FIRST_OFFICER, ...INFRA],
  "item.log_time": [...WRITERS, ...INFRA],
  "cr.design_edit": EDITORS,
  "sd.reply": SERVICE_DESK,
  "cm.view": SERVICE_DESK,
  "cm.fields_edit": SERVICE_DESK,
  "cm.month_close": ["ProjectManager", "ProjectDirector"],
  "cm.month_reopen": ["ProjectDirector"],
  // Ruling 2026-10-03 (Revision 81): a locked row, the Project Director and Project Manager only.
  "sprint.manage": ["ProjectDirector", "ProjectManager"],
  // Batch 16 (GRF-25): Chief Pilot, Capt., FO and Analyst put items into sprints; Member, QA, Passengers and ATC do not.
  "sprint.assign": ["ProjectDirector", "ProjectManager", "FirstOfficer", "Analyst"],
  "board.create": ["ProjectManager", "ProjectDirector"],
  "board.mode": ["ProjectManager", "ProjectDirector"],
  // Revision 89 (ruling 2026-10-03, "any member must be able to do"): every internal project role.
  "deployment.emergency_approve": INTERNAL,
  // Revision 107 amendment: every ATC type creates and updates Issue and Action items. DevOps and the global ATC grant are not
  // project roles, so only their project-role holders are listed here.
  "item.create_raid": ["ProjectAdmin", "InfraManager"],
  "item.edit_raid": ["ProjectAdmin", "InfraManager"],
  // Batch 16 (ruling 2026-10-09): every role (DevOps and the global ATC grant hold it too, outside projectRoles).
  "item.export": [...EDITORS, "FirstOfficer", "Viewer", "Partner", "ProjectAdmin", "InfraManager"],
  // Batch 19 F3 (SA-B19-EVENTS §2.3): internal only; ATC (Infra) keeps the DevOps column, so it does not see events by default.
  "event.view": [...EDITORS, "FirstOfficer", "Viewer", "ProjectAdmin"],
  "event.manage": ["ProjectDirector", "ProjectManager", "QA"],
  // Ruling R8: a locked row, the Project Director and Project Manager only.
  "event.external.manage": ["ProjectDirector", "ProjectManager"],
  // Batch 20 B20-1 (sidebar by role): every role that saw the entry before, but ATC (Admin) and ATC (Infra) keep only Issues and
  // actions (Admin), Deployments and Deployment backlog. DevOps and the global ATC grant hold their menu too, outside projectRoles.
  "menu.change_management": MENU_CREW,
  "menu.issues_actions": [...MENU_CREW, "ProjectAdmin"],
  "menu.backlog": MENU_CREW,
  "menu.board": MENU_CREW,
  "menu.reports": MENU_CREW,
  "menu.service_desk": MENU_CREW,
  "menu.events": [...EDITORS, "FirstOfficer", "Viewer"],
  "menu.deployments": [...MENU_CREW, "ProjectAdmin", "InfraManager"],
  "menu.deployment_backlog": [...MENU_CREW.filter((role) => role !== "Partner"), "ProjectAdmin", "InfraManager"],
  // The RAID placeholder (no page): no default holder.
  "menu.raid": [],
  // Batch 26 (rulings of 20 Oct 2026): the weekly status, the Capt.s and the Chief Pilot only. status.refreeze is the System
  // Admin's alone, who is never in projectRoles.
  "menu.project_status": ["ProjectDirector", "ProjectManager"],
  "status.view": ["ProjectDirector", "ProjectManager"],
  "status.edit": ["ProjectDirector", "ProjectManager"],
  "status.portfolio": ["ProjectDirector"],
  "status.freeze": ["ProjectDirector"],
  "status.refreeze": [],
  // Batch 26 R2: ATC (PMO) holds it too, as the global grant (never in projectRoles).
  "status.comment": ["ProjectDirector", "ProjectManager"],
  "status.rows": ["ProjectDirector", "ProjectManager"],
  "status.contributors": ["ProjectDirector", "ProjectManager"],
  "status.decide": ["ProjectDirector"],
  // Wiki W1 (§6.1): read and export for every internal role (DevOps and the global ATC grant too, outside projectRoles); write for the
  // crew, the FO, ATC (Admin) and ATC (Infra); publish never the FO; restrict and manage the leads and ATC (Admin).
  "wiki.view": [...EDITORS, "FirstOfficer", "Viewer", "ProjectAdmin", "InfraManager"],
  "wiki.edit": [...EDITORS, "FirstOfficer", "ProjectAdmin", "InfraManager"],
  "wiki.publish": [...EDITORS, "ProjectAdmin", "InfraManager"],
  "wiki.comment": [...EDITORS, "FirstOfficer", "ProjectAdmin", "InfraManager"],
  "wiki.restrict": ["ProjectDirector", "ProjectManager", "ProjectAdmin"],
  "wiki.manage": ["ProjectDirector", "ProjectManager", "ProjectAdmin"],
  "wiki.export": [...EDITORS, "FirstOfficer", "Viewer", "ProjectAdmin", "InfraManager"],
  "menu.wiki": [...EDITORS, "FirstOfficer", "Viewer", "ProjectAdmin", "InfraManager"],
  "menu.company_wiki": [...EDITORS, "FirstOfficer", "Viewer", "ProjectAdmin", "InfraManager"],
  // Presentations (PRESENT-SPEC-20261028 §8.3): never Partner, FO, DevOps, ATC (Infra) or ATC (PMO).
  "present.view": ["ProjectDirector", "ProjectManager", "Analyst", "Member", "QA", "ServiceDeskAgent", "Viewer", "ProjectAdmin"],
  "present.upload": ["ProjectDirector", "ProjectManager", "Analyst", "Member"],
  "present.control": ["ProjectDirector", "ProjectManager", "Analyst", "Member"],
  "menu.presentations": ["ProjectDirector", "ProjectManager", "Analyst", "Member", "QA", "ServiceDeskAgent", "Viewer", "ProjectAdmin"],
};

/** The codes the caller holds on a project: the server's list when it sent one, else the defaults for the roles. */
export function codesIn(me: Pick<Me, "projectRoles" | "projectPermissions">, projectKey: string): ReadonlySet<string> {
  const sent = me.projectPermissions?.find((entry) => entry.projectKey === projectKey);
  if (sent) return new Set(sent.codes);
  const roles = me.projectRoles.find((entry) => entry.projectKey === projectKey)?.roles ?? [];
  const codes = new Set<string>();
  for (const [code, holders] of Object.entries(DEFAULT_CAPABILITY_HOLDERS)) {
    if (roles.some((role) => holders.includes(role))) codes.add(code);
  }
  return codes;
}
